"use client";

import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Key, MessageSquare, Code, LogOut, Coins, Menu, User, CreditCard, HandMetal } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, loading, logout } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const t = useTranslations("dashboard");
  const tNav = useTranslations("nav");

  const navItems = [
    { href: "/dashboard" as const, label: t("nav.overview"), icon: LayoutDashboard },
    { href: "/dashboard/keys" as const, label: t("nav.apiKeys"), icon: Key },
    { href: "/dashboard/billing" as const, label: t("nav.billing"), icon: CreditCard },
    { href: "/dashboard/profile" as const, label: t("nav.profile"), icon: User },
    { href: "/dashboard/teamwork" as const, label: t("nav.teamwork"), icon: HandMetal },
    { href: "/chat" as const, label: t("nav.chat"), icon: MessageSquare },
    { href: "/playground" as const, label: t("nav.playground"), icon: Code },
  ];

  // Open sidebar by default on desktop
  useEffect(() => {
    if (window.innerWidth >= 768) setSidebarOpen(true);
  }, []);

  useEffect(() => {
    if (!loading && !user) router.push("/auth/login");
  }, [loading, user, router]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    );
  }

  // Format token: tampilkan 4 desimal di sidebar (ringkas) dengan thousand separators
  const formatToken = (balance: number) => {
    let formatted;
    if (balance === 0) formatted = "0.0000";
    else if (balance < 0.0001) formatted = balance.toExponential(2);
    else formatted = balance.toFixed(4);

    // Add thousand separators
    const [whole, decimal] = formatted.split(".");
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimal ? `${withCommas}.${decimal}` : withCommas;
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:relative z-40 h-full md:h-auto
          border-r bg-background md:bg-muted/30 flex flex-col
          transition-transform duration-200
          w-64
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden md:border-r-0"}
        `}
      >
        <div className="min-w-[256px]">
          <div className="p-4 border-b flex items-center gap-2">
            <button onClick={() => setSidebarOpen(false)} className="p-1 rounded hover:bg-muted" title="Close sidebar">
              <Menu className="w-5 h-5" />
            </button>
            <Link href="/dashboard">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/ethan-logo-text.png" alt="Ethan AI" className="h-7 w-auto" />
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link key={item.href} href={item.href} onClick={() => { if (window.innerWidth < 768) setSidebarOpen(false); }}>
                <Button
                  variant={pathname === item.href ? "secondary" : "ghost"}
                  className="w-full justify-start gap-2"
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t space-y-2">
            <div className="flex items-center gap-2 text-sm px-3">
              <Coins className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t("credit")}</span>
              <span className="font-medium font-mono">{formatToken(user.tokenBalance)}</span>
              <span className="text-xs text-muted-foreground">BKU</span>
            </div>
            <div className="text-xs text-muted-foreground px-3 truncate">{user.email}</div>
            <div className="px-1">
              <LanguageSwitcher />
            </div>
            <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground" onClick={() => { logout(); router.push("/"); }}>
              <LogOut className="w-4 h-4" /> {tNav("logout")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-4 md:p-8 overflow-auto w-full">
        {!sidebarOpen && (
          <button onClick={() => setSidebarOpen(true)} className="mb-4 p-1 rounded hover:bg-muted md:block" title="Open sidebar">
            <Menu className="w-5 h-5" />
          </button>
        )}
        {children}
      </main>
    </div>
  );
}
